import { FormBlock } from "vmd-types";

import { STRAPI_URL } from "../env";

export async function importItems(
  file: File,
  mapping: Record<string, string>,
  projectId?: string,
  headerRowIndex?: number,
  dataStartRowIndex?: number
) {
  const formData = new FormData();
  formData.append("files", file);
  formData.append("mapping", JSON.stringify(mapping));

  if (projectId) {
    formData.append("projectId", projectId);
  }
  if (headerRowIndex !== undefined) {
    formData.append("headerRowIndex", headerRowIndex.toString());
  }
  if (dataStartRowIndex !== undefined) {
    formData.append("dataStartRowIndex", dataStartRowIndex.toString());
  }

  const url = `${STRAPI_URL}/excel-import/import-with-mapping`;

  try {
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("[ExcelImport] Server error:", errorText);
      throw new Error(
        `Server error ${response.status}: ${response.statusText}. ${errorText}`
      );
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error("[ExcelImport] Network/Parse error:", error);
    throw error;
  }
}

export async function parseOeSummary(file: File): Promise<{ names: string[] }> {
  const formData = new FormData();
  formData.append("files", file);

  const url = `${STRAPI_URL}/excel-import/oe-summary`;
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
    body: formData,
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error("[OeUpload] Server error:", errorText);
    throw new Error(
      `Failed to parse OE_Summary: ${response.status} ${response.statusText}. ${errorText}`
    );
  }

  return response.json();
}

export type DistributionImportSummary = {
  channelsCreated: number;
  oesCreated: number;
  businessApps: number;
  itServices: number;
  oesTouched: number;
  skippedRows: number;
};

// Bulk-imports the distribution apps (DC Business Apps + IT Services) from
// the three tabs of the scope workbook. Resolves with the import summary; on failure throws an Error whose
// `details` carries the server's per-check messages.
export async function importDistributionApps(
  file: File,
  resetData = false
): Promise<DistributionImportSummary> {
  const formData = new FormData();
  formData.append("files", file);
  formData.append("resetData", resetData ? "true" : "false");

  const url = `${STRAPI_URL}/basic-item/import-distribution?resetData=${
    resetData ? "true" : "false"
  }`;
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
    body: formData,
  });

  if (!response.ok) {
    let message = `Import failed: ${response.status} ${response.statusText}`;
    let details: string[] = [];
    try {
      const body = await response.json();
      if (body?.message) message = body.message;
      if (Array.isArray(body?.details)) details = body.details;
    } catch {
      // keep the default message if the body isn't JSON
    }
    const error = new Error(message) as Error & { details?: string[] };
    error.details = details;
    throw error;
  }

  return response.json();
}

export type DcRelevanceChange = {
  reportingItemId: string;
  projectId: string;
  projectName: string;
  appName: string;
  customId: string;
  adoitIds: string[];
  from: "Yes" | "No" | null;
  to: "Yes" | "No";
};

export type DcRelevanceImportResult = {
  dryRun: boolean;
  excelIdCount: number;
  itemsInScope: number;
  unchanged: number;
  unchangedItems: DcRelevanceChange[];
  changes: DcRelevanceChange[];
  excelIdsNotFound: {
    id: string;
    name: string;
    projectId?: string;
    projectName?: string;
  }[];
};

// Sets "DC Relevance" for the business apps of the given OEs from an ADOIT
// export: Yes when the app's ADOIT ID is in the file, No otherwise. A dry run
// returns the planned changes without writing anything.
export async function importDcRelevance(
  file: File,
  projectIds: string[],
  dryRun: boolean
): Promise<DcRelevanceImportResult> {
  const formData = new FormData();
  formData.append("files", file);
  formData.append("projectIds", JSON.stringify(projectIds));
  formData.append("dryRun", dryRun ? "true" : "false");

  const response = await fetch(`${STRAPI_URL}/dc-relevance-import`, {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    body: formData,
  });

  if (!response.ok) {
    let message = `Import failed: ${response.status} ${response.statusText}`;
    try {
      const body = await response.json();
      if (body?.message) message = body.message;
    } catch {
      // keep the default message if the body isn't JSON
    }
    throw new Error(message);
  }

  return response.json();
}

// Parses the "Questionnaire Fields" sheet of the VMD data-model workbook into
// form blocks (each group becomes a block, its fields become questions). Returns
// the parsed blocks for the caller to merge into configuration.trackingItemForms;
// nothing is persisted here.
export async function importQuestionnaireFormBlocks(
  file: File
): Promise<FormBlock[]> {
  const formData = new FormData();
  formData.append("files", file);

  const url = `${STRAPI_URL}/excel-import/questionnaire-form-blocks`;
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
    body: formData,
  });

  if (!response.ok) {
    let message = `Import failed: ${response.status} ${response.statusText}`;
    try {
      const body = await response.json();
      if (body?.message) message = body.message;
      // Per-row entity_ref failures arrive as details; keep every line.
      if (Array.isArray(body?.details) && body.details.length > 0) {
        message = `${message}\n${body.details.join("\n")}`;
      }
    } catch {
      // keep the default message if the body isn't JSON
    }
    throw new Error(message);
  }

  return response.json();
}

export async function parseExcelHeaders(file: File) {
  const formData = new FormData();
  formData.append("files", file);

  try {
    const url = `${STRAPI_URL}/excel-import/headers`;
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("[ExcelImport] Headers server error:", errorText);
      throw new Error(
        `Failed to parse Excel headers: ${response.status} ${response.statusText}. ${errorText}`
      );
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error("Error parsing Excel headers:", error);
    throw error;
  }
}
